import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Table,
  Tag,
  Space,
  Modal,
  message,
  Descriptions,
  Typography,
  Spin,
  Empty,
  Row,
  Col,
  Statistic,
  Collapse,
  Popconfirm
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
  ReloadOutlined,
  RobotOutlined,
  WarningOutlined,
  DeleteOutlined,
  UserOutlined
} from '@ant-design/icons';
import API from '../services/api';
import dayjs from 'dayjs';
import './AdminAttackAnalysis.css';

const { Title, Text, Paragraph } = Typography;

const AdminAttackAnalysis = () => {
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 20;

  // 分析结果模态框
  const [resultModal, setResultModal] = useState({
    visible: false,
    loading: false,
    data: null
  });

  // 获取所有分析记录
  const fetchAnalyses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/attack-analysis/admin/all', {
        params: {
          page: currentPage,
          limit: limit
        }
      });

      if (response.data.code === 0) {
        setAnalyses(response.data.data);
        setTotalRecords(response.data.pagination.total);
      } else {
        message.error(response.data.message || '获取分析记录失败');
      }
    } catch (error) {
      console.error('获取分析记录失败:', error);
      message.error('获取分析记录失败');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  // 查看分析详情
  const viewAnalysis = async (analysisId) => {
    setResultModal({ visible: true, loading: true, data: null });
    try {
      const response = await API.get(`/api/attack-analysis/${analysisId}`);

      if (response.data.code === 0) {
        setResultModal({
          visible: true,
          loading: false,
          data: response.data.data
        });
      } else {
        message.error(response.data.message || '获取分析详情失败');
        setResultModal({ visible: false, loading: false, data: null });
      }
    } catch (error) {
      console.error('获取分析详情失败:', error);
      message.error('获取分析详情失败');
      setResultModal({ visible: false, loading: false, data: null });
    }
  };

  // 删除分析记录
  const handleDelete = async (analysisId) => {
    try {
      const response = await API.delete(`/api/attack-analysis/${analysisId}`);

      if (response.data.code === 0) {
        message.success('删除成功');
        fetchAnalyses();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 导出PDF
  const exportPDF = async (analysisId, targetIP) => {
    try {
      message.loading({ content: '正在生成PDF...', key: 'pdf' });
      
      const response = await API.get(`/api/attack-analysis/${analysisId}/export-pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attack_analysis_${targetIP}_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: 'PDF导出成功', key: 'pdf' });
    } catch (error) {
      console.error('导出PDF失败:', error);
      message.error({ content: '导出PDF失败', key: 'pdf' });
    }
  };

  // 获取状态标签
  const getStatusTag = (status) => {
    const statusMap = {
      completed: { color: 'success', text: '已完成' },
      failed: { color: 'error', text: '失败' },
      processing: { color: 'processing', text: '处理中' }
    };
    const config = statusMap[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 表格列定义
  const columns = [
    {
      title: '用户',
      dataIndex: ['userId', 'username'],
      key: 'username',
      width: 120,
      render: (username, record) => (
        <Space direction="vertical" size={0} style={{ display: 'flex' }}>
          <Text strong>{username}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.userId?.email}
          </Text>
        </Space>
      )
    },
    {
      title: '目标IP',
      dataIndex: 'targetIP',
      key: 'targetIP',
      width: 150,
      render: (ip) => <Text strong>{ip}</Text>
    },
    {
      title: '分析时间范围',
      key: 'dateRange',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ display: 'flex' }}>
          <Text style={{ fontSize: '12px' }}>
            {dayjs(record.analysisTimeRange?.startDate).format('YYYY-MM-DD')}
          </Text>
          <Text style={{ fontSize: '12px' }}>
            {dayjs(record.analysisTimeRange?.endDate).format('YYYY-MM-DD')}
          </Text>
        </Space>
      )
    },
    {
      title: '攻击事件数',
      dataIndex: ['attackStatistics', 'totalAttacks'],
      key: 'totalAttacks',
      width: 120,
      align: 'center',
      sorter: (a, b) => (a.attackStatistics?.totalAttacks || 0) - (b.attackStatistics?.totalAttacks || 0),
      render: (count) => (
        <Tag color={count > 0 ? 'red' : 'default'}>
          {count || 0}
        </Tag>
      )
    },
    {
      title: '威胁等级',
      dataIndex: ['aiAnalysis', 'threatLevel'],
      key: 'threatLevel',
      width: 100,
      align: 'center',
      render: (level) => {
        const levelMap = {
          '严重': { color: 'red', text: '严重' },
          '高': { color: 'orange', text: '高' },
          '中': { color: 'gold', text: '中' },
          '低': { color: 'blue', text: '低' },
          '未知': { color: 'default', text: '未知' }
        };
        const config = levelMap[level] || { color: 'default', text: level || '未知' };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status) => getStatusTag(status)
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => viewAnalysis(record._id)}
          >
            查看
          </Button>
          {record.status === 'completed' && (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => exportPDF(record._id, record.targetIP)}
            >
              导出
            </Button>
          )}
          <Popconfirm
            title="确定要删除这条分析记录吗？"
            onConfirm={() => handleDelete(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 渲染分析结果
  const renderAnalysisResult = () => {
    const data = resultModal.data;
    if (!data) return null;

    return (
      <div className="analysis-result" style={{ 
        maxHeight: 'calc(100vh - 300px)', 
        overflowY: 'auto', 
        paddingRight: '8px' 
      }}>
        {/* 基本信息 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="用户">
              <Space>
                <UserOutlined />
                {data.userId?.username}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="邮箱">
              {data.userId?.email}
            </Descriptions.Item>
            <Descriptions.Item label="目标IP">{data.targetIP}</Descriptions.Item>
            <Descriptions.Item label="分析时间">
              {dayjs(data.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="分析范围">
              {dayjs(data.analysisTimeRange?.startDate).format('YYYY-MM-DD')} 至 {dayjs(data.analysisTimeRange?.endDate).format('YYYY-MM-DD')}
            </Descriptions.Item>
            <Descriptions.Item label="AI模型">
              {data.aiModel}
            </Descriptions.Item>
            <Descriptions.Item label="响应时间">
              {data.responseTime ? `${(data.responseTime / 1000).toFixed(2)}秒` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              {getStatusTag(data.status)}
            </Descriptions.Item>
            {data.error && (
              <Descriptions.Item label="错误信息" span={2}>
                <Text type="danger">{data.error}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* 攻击统计 */}
        {data.attackStatistics && (
          <Card title="攻击统计" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总攻击次数"
                  value={data.attackStatistics.totalAttacks || 0}
                  prefix={<WarningOutlined />}
                  styles={{ content: { color: '#ff4d4f' } }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="最大流量"
                  value={data.attackStatistics.maxAttackValue || 0}
                  suffix="Gbps"
                  styles={{ content: { color: '#fa8c16' } }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="平均流量"
                  value={data.attackStatistics.avgAttackValue || 0}
                  suffix="Gbps"
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="攻击天数"
                  value={data.attackStatistics.attackDays || 0}
                  suffix="天"
                />
              </Col>
            </Row>
            {data.attackStatistics.peakAttackTime && (
              <div style={{ marginTop: 16, padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                <Text strong>攻击高峰：</Text>
                <Text style={{ marginLeft: 8 }}>{data.attackStatistics.peakAttackTime}</Text>
              </div>
            )}
          </Card>
        )}

        {/* 威胁等级和攻击类型 */}
        {data.aiAnalysis && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={12}>
                <div style={{ padding: '12px', background: '#fff1f0', borderRadius: '4px', border: '1px solid #ffccc7' }}>
                  <Text strong style={{ fontSize: '14px' }}>威胁等级：</Text>
                  <Tag color="red" style={{ marginLeft: 8, fontSize: '14px' }}>
                    {data.aiAnalysis.threatLevel || '未知'}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ padding: '12px', background: '#f0f5ff', borderRadius: '4px', border: '1px solid #adc6ff' }}>
                  <Text strong style={{ fontSize: '14px' }}>攻击类型：</Text>
                  <Space style={{ marginLeft: 8 }}>
                    {data.aiAnalysis.attackTypes && data.aiAnalysis.attackTypes.length > 0 ? (
                      data.aiAnalysis.attackTypes.map((type, idx) => (
                        <Tag key={idx} color="blue">{type}</Tag>
                      ))
                    ) : (
                      <Text type="secondary">暂无</Text>
                    )}
                  </Space>
                </div>
              </Col>
            </Row>
          </Card>
        )}

        {/* AI分析结果 */}
        {data.aiAnalysis && (
          <Collapse 
            defaultActiveKey={['1', '2', '3', '4', '5']} 
            style={{ marginBottom: 16 }}
            items={[
              data.aiAnalysis.attackCharacteristics && {
                key: '1',
                label: '攻击特征分析',
                children: (
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {data.aiAnalysis.attackCharacteristics}
                  </Paragraph>
                )
              },
              data.aiAnalysis.attackPatterns && {
                key: '2',
                label: '攻击模式分析',
                children: (
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {data.aiAnalysis.attackPatterns}
                  </Paragraph>
                )
              },
              data.aiAnalysis.trendAnalysis && {
                key: '3',
                label: '趋势分析',
                children: (
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {data.aiAnalysis.trendAnalysis}
                  </Paragraph>
                )
              },
              data.aiAnalysis.overallAssessment && {
                key: '4',
                label: '总体评估',
                children: (
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {data.aiAnalysis.overallAssessment}
                  </Paragraph>
                )
              },
              data.aiAnalysis.protectionSuggestions && {
                key: '5',
                label: '防护建议',
                children: (
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                    {data.aiAnalysis.protectionSuggestions}
                  </Paragraph>
                )
              }
            ].filter(Boolean)}
          />
        )}

      </div>
    );
  };

  return (
    <div className="admin-attack-analysis">
      <div className="page-header">
        <Title level={2}>
          <RobotOutlined /> AI攻击分析管理
        </Title>
        <Paragraph type="secondary">
          查看和管理所有用户的AI攻击分析记录
        </Paragraph>
      </div>

      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>所有分析记录</span>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAnalyses}
            loading={loading}
          >
            刷新
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={analyses}
          rowKey="_id"
          loading={loading}
          pagination={{
            current: currentPage,
            total: totalRecords,
            pageSize: limit,
            onChange: (page) => setCurrentPage(page),
            showTotal: (total) => `共 ${total} 条记录`
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 分析结果模态框 */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            <span>AI分析结果</span>
          </Space>
        }
        open={resultModal.visible}
        onCancel={() => setResultModal({ visible: false, loading: false, data: null })}
        footer={[
          <Button key="close" onClick={() => setResultModal({ visible: false, loading: false, data: null })}>
            关闭
          </Button>,
          resultModal.data && resultModal.data.status === 'completed' && (
            <Button
              key="export"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                exportPDF(resultModal.data._id, resultModal.data.targetIP);
              }}
            >
              导出PDF
            </Button>
          )
        ]}
        width={900}
      >
        <Spin spinning={resultModal.loading}>
          {resultModal.data ? renderAnalysisResult() : (
            <Empty description="暂无数据" />
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default AdminAttackAnalysis;

